import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, InputGroup, Dropdown, Modal, Tab, Tabs, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { adminService } from '../../services/adminService';
import BannerModal from './BannerModal';
import BannerGroupModal from './BannerGroupModal';
import toast from 'react-hot-toast';

const Banners: React.FC = () => {
  const [banners, setBanners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('banners');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedBanner, setSelectedBanner] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Utility function to truncate text with ellipsis
  const truncateWithEllipsis = (text: string, maxLength: number): string => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    loadBanners();
  }, [currentPage, filterPosition, filterStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadBanners();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await adminService.getBanners({
        search: searchTerm,
        status: filterStatus === 'all' ? undefined : filterStatus,
        position: filterPosition === 'all' ? undefined : filterPosition,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response.success && response.data) {
        setBanners(response.data);
      }
    } catch (error: any) {
      console.error('Error loading banners:', error);
      toast.error('Failed to load banners');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBanner = async (banner: any) => {
    if (!window.confirm(`Are you sure you want to delete banner "${banner.title}"?`)) {
      return;
    }

    try {
      const response = await adminService.deleteBanner(banner.id);
      if (response.success) {
        toast.success(`Banner "${banner.title}" deleted successfully!`);
        loadBanners();
      }
    } catch (error: any) {
      toast.error('Failed to delete banner');
    }
  };

  const handleSaveBanner = async (bannerData: any) => {
    try {
      let response;
      if (modalMode === 'add') {
        response = await adminService.createBanner(bannerData);
        if (response.success) {
          toast.success('Banner created successfully!');
        }
      } else {
        response = await adminService.updateBanner(selectedBanner.id, bannerData);
        if (response.success) {
          toast.success('Banner updated successfully!');
        }
      }
      setShowModal(false);
      loadBanners();
    } catch (error: any) {
      toast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} banner`);
    }
  };
  
  // Banner Group states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState<'add' | 'edit'>('add');
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [showGroupDeleteConfirm, setShowGroupDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<any>(null);
  const [bannerGroups] = useState([
    {
      id: 'group_001',
      name: 'Home Page Hero Carousel',
      description: 'Main carousel on the home page',
      position: 'Hero',
      isActive: true,
      autoPlay: true,
      interval: 5,
      showIndicators: true,
      showControls: true,
      bannerIds: ['banner_001', 'banner_002', 'banner_003'],
      createdAt: new Date('2024-01-10')
    },
    {
      id: 'group_002',
      name: 'Promotional Offers Carousel',
      description: 'Rotating promotional banners',
      position: 'Promotional',
      isActive: true,
      autoPlay: true,
      interval: 7,
      showIndicators: false,
      showControls: true,
      bannerIds: ['banner_004'],
      createdAt: new Date('2024-01-12')
    }
  ]);
  
  const filteredBanners = banners.filter(banner => {
    const matchesPosition = filterPosition === 'all' || 
      banner.position?.toLowerCase() === filterPosition.toLowerCase();
    const matchesStatus = filterStatus === 'all' || 
      banner.status?.toLowerCase() === filterStatus.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      banner.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      banner.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesPosition && matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      'Active': 'success',
      'Inactive': 'secondary',
      'Scheduled': 'warning'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getPositionBadge = (position: string) => {
    const variants: Record<string, string> = {
      'Hero': 'primary',
      'Featured': 'info',
      'Promotional': 'warning',
      'Sidebar': 'secondary'
    };
    return <Badge bg={variants[position] || 'secondary'}>{position}</Badge>;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return '—';
    }
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleAddBanner = () => {
    setModalMode('add');
    setSelectedBanner(null);
    setShowModal(true);
  };

  const handleEditBanner = (banner: any) => {
    setModalMode('edit');
    setSelectedBanner(banner);
    setShowModal(true);
  };

  // Banner Group handlers
  const handleAddGroup = () => {
    setGroupModalMode('add');
    setSelectedGroup(null);
    setShowGroupModal(true);
  };

  const handleEditGroup = (group: any) => {
    setGroupModalMode('edit');
    setSelectedGroup(group);
    setShowGroupModal(true);
  };

  const handleDeleteGroup = (group: any) => {
    setGroupToDelete(group);
    setShowGroupDeleteConfirm(true);
  };

  const handleSaveGroup = (groupData: any) => {
    if (groupModalMode === 'add') {
      toast.success('Banner carousel created successfully!');
      console.log('Creating new carousel:', groupData);
    } else {
      toast.success('Banner carousel updated successfully!');
      console.log('Updating carousel:', groupData);
    }
    setShowGroupModal(false);
  };

  const confirmGroupDelete = () => {
    toast.success(`Banner carousel "${groupToDelete.name}" deleted successfully!`);
    console.log('Deleting carousel:', groupToDelete);
    setShowGroupDeleteConfirm(false);
    setGroupToDelete(null);
  };

  const getBannerById = (id: string) => {
    return banners.find((b: any) => b.id === id);
  };

  return (
    <>
      <Helmet>
        <title>Banners - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Banners </h2>
            <p className="text-muted mb-0">Manage promotional banners </p>
          </div>
          <div className="d-flex gap-2">
            {/* <Button variant="outline-primary" onClick={handleAddGroup}>
              <i className="bi-collection me-1"></i>
              Create Carousel
            </Button> */}
            <Button variant="primary" onClick={handleAddBanner}>
              <i className="bi-plus-circle me-1"></i>
              Add Banner
            </Button>
          </div>
        </div>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'banners')} className="mb-3" variant="pills">
          <Tab eventKey="banners" title={`Individual Banners (${filteredBanners.length})`}>
            <Card className="mt-3">
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0">All Banners ({filteredBanners.length})</h5>
              </Col>
              <Col md={2}>
                <Form.Select 
                  value={filterPosition} 
                  onChange={(e) => setFilterPosition(e.target.value)}
                >
                  <option value="all">All Positions</option>
                  <option value="hero">Hero</option>
                  {/* <option value="featured">Featured</option> */}
                  {/* <option value="promotional">Promotional</option> */}
                  {/* <option value="sidebar">Sidebar</option> */}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="scheduled">Scheduled</option> 
                </Form.Select>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control 
                    type="text" 
                    placeholder="Search banners..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 banner-table">
              <thead className="table-light">
                <tr>
                  <th>Banner</th>
                  <th>Position</th>
                  <th>Status</th>
                  <th>Period</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBanners.map((banner) => (
                  <tr key={banner.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {banner.imageUrl ? (
                          <img 
                            src={banner.imageUrl} 
                            alt={banner.title}
                            className="me-3 rounded"
                            style={{ width: '60px', height: '40px', objectFit: 'cover' }}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const nextElement = target.nextElementSibling as HTMLElement;
                              if (nextElement) {
                                nextElement.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div 
                          className="bg-light rounded d-flex align-items-center justify-content-center me-3"
                          style={{ width: '60px', height: '40px', display: banner.imageUrl ? 'none' : 'flex' }}
                        >
                          <i className="bi-image text-muted"></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-medium">{banner.title}</div>
                          <small className="text-muted d-none d-md-block">
                            {truncateWithEllipsis(banner.description, 70)}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>{getPositionBadge(banner.position)}</td>
                    <td>{getStatusBadge(banner.status)}</td>
                    <td>
                      <div>
                        <small className="text-muted">From:</small>
                        <div>{formatDate(banner.startDate)}</div>
                        <small className="text-muted">To:</small>
                        <div>{formatDate(banner.endDate)}</div>
                      </div>
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${banner.id}`}>
                          <i className="bi-three-dots"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleEditBanner(banner)}>
                            <i className="bi-pencil me-2"></i>Edit Banner
                          </Dropdown.Item>
                          {/* <Dropdown.Item>
                            <i className="bi-graph-up me-2"></i>View Analytics
                          </Dropdown.Item> */}
                          <Dropdown.Divider />
                          <Dropdown.Item className="text-danger" onClick={() => handleDeleteBanner(banner)}>
                            <i className="bi-trash me-2"></i>Delete Banner
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
            </Card>
          </Tab>

          {/* <Tab eventKey="carousels" title={`Carousels (${bannerGroups.length})`}>
            <Card className="mt-3">
              <Card.Body>
                <Row>
                  {bannerGroups.map((group) => (
                    <Col key={group.id} md={6} className="mb-4">
                      <Card className="h-100">
                        <Card.Header>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-0">{group.name}</h6>
                              <small className="text-muted">{group.description}</small>
                            </div>
                            <div>
                              {group.isActive ? (
                                <Badge bg="success">Active</Badge>
                              ) : (
                                <Badge bg="secondary">Inactive</Badge>
                              )}
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <Row className="mb-3">
                            <Col md={6}>
                              <small className="text-muted">Position</small>
                              <div className="fw-medium">{group.position}</div>
                            </Col>
                            <Col md={6}>
                              <small className="text-muted">Banners</small>
                              <div className="fw-medium">{group.bannerIds.length} items</div>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col md={6}>
                              <small className="text-muted">Auto-play</small>
                              <div>
                                {group.autoPlay ? (
                                  <Badge bg="success">
                                    <i className="bi-play-fill me-1"></i>Yes ({group.interval}s)
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary">No</Badge>
                                )}
                              </div>
                            </Col>
                            <Col md={6}>
                              <small className="text-muted">Controls</small>
                              <div>
                                {group.showIndicators && <Badge bg="info" className="me-1">Dots</Badge>}
                                {group.showControls && <Badge bg="info">Arrows</Badge>}
                              </div>
                            </Col>
                          </Row>
                          <div className="mb-3">
                            <small className="text-muted">Banners in Carousel:</small>
                            <div className="mt-2">
                              {group.bannerIds.map((bannerId, index) => {
                                const banner = getBannerById(bannerId);
                                return banner ? (
                                  <div key={bannerId} className="d-flex align-items-center mb-2">
                                    <Badge bg="primary" className="me-2">{index + 1}</Badge>
                                    <div 
                                      className="bg-light rounded d-flex align-items-center justify-content-center me-2"
                                      style={{ width: '30px', height: '20px' }}
                                    >
                                      <i className="bi-image" style={{ fontSize: '0.7rem' }}></i>
                                    </div>
                                    <small>{banner.title}</small>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          </div>
                        </Card.Body>
                        <Card.Footer className="bg-white">
                          <div className="d-flex gap-2">
                            <Button variant="outline-primary" size="sm" onClick={() => handleEditGroup(group)} className="flex-grow-1">
                              <i className="bi-pencil me-1"></i>Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteGroup(group)}>
                              <i className="bi-trash"></i>
                            </Button>
                          </div>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                  
                  {bannerGroups.length === 0 && (
                    <Col md={12}>
                      <Card className="text-center py-5">
                        <Card.Body>
                          <i className="bi-collection display-1 text-muted mb-3"></i>
                          <h5>No Carousels Yet</h5>
                          <p className="text-muted">Create your first banner carousel to group multiple banners together.</p>
                          <Button variant="primary" onClick={handleAddGroup}>
                            <i className="bi-plus-circle me-1"></i>Create First Carousel
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          </Tab> */}
        </Tabs>

        {/* Add/Edit Banner Modal */}
        <BannerModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSaveBanner}
          banner={selectedBanner}
          mode={modalMode}
        />


        {/* Add/Edit Banner Group Modal */}
        <BannerGroupModal
          show={showGroupModal}
          onHide={() => setShowGroupModal(false)}
          onSave={handleSaveGroup}
          group={selectedGroup}
          mode={groupModalMode}
        />

        {/* Delete Carousel Confirmation Modal */}
        <Modal show={showGroupDeleteConfirm} onHide={() => setShowGroupDeleteConfirm(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete Carousel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete the carousel <strong>"{groupToDelete?.name}"</strong>?</p>
            <p className="text-muted">
              This will not delete the individual banners, only the carousel group.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGroupDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmGroupDelete}>
              Delete Carousel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default Banners;
